/**
 * Zustand game store — reactive state for the UI.
 * The GameController is the source of truth; this store
 * reflects its state for React components.
 */

import { create } from 'zustand';

export const useGameStore = create((set, get) => ({
  // Navigation state
  currentView: 'home', // 'home' | 'builder' | 'game' | 'stats'
  gameMode: 'custom',

  // Target selection
  targets: [],
  targetSlots: 2,

  // Game options
  gameOptions: {
    crewConnections: false,
    scoring: 'standard',
    fogOfWar: false,
    ghostMode: false,
    hintMode: 'unlimited',
    hintPool: 5,
    timeLimit: 0,
    pathConstraints: { genre: null, decade: null },
    connectionPreview: true,
    toastNotifications: true,
    isExploration: false,
  },

  // In-game state (updated from GameController events)
  gameActive: false,
  gameController: null,
  stats: { actors: 0, titles: 0, hintsUsed: 0, won: false, bestPath: null, elapsed: 0 },
  guessHistory: [],
  won: false,
  winResult: null,
  firstWin: false,
  isNewBest: false,
  loading: false,
  error: null,

  // Win panel state
  winPanelState: 'closed', // 'closed' | 'peeked' | 'expanded'

  // Toast messages
  toasts: [],

  // Multiplayer
  isMultiplayer: false,
  roomCode: null,
  players: [],

  // Actions
  setView: (view) => set({ currentView: view }),

  setGameMode: (mode) => set({ gameMode: mode }),

  setTargets: (targets) => set({ targets }),

  addTarget: (target) => {
    const { targets, targetSlots } = get();
    if (targets.length >= targetSlots) return;
    if (targets.some(t => t.id === target.id && t.type === target.type)) return;
    set({ targets: [...targets, target] });
  },

  removeTarget: (index) => {
    const { targets } = get();
    set({ targets: targets.filter((_, i) => i !== index) });
  },

  reorderTargets: (fromIndex, toIndex) => {
    const { targets } = get();
    const newTargets = [...targets];
    const [moved] = newTargets.splice(fromIndex, 1);
    newTargets.splice(toIndex, 0, moved);
    set({ targets: newTargets });
  },

  setTargetSlots: (count) => set({ targetSlots: Math.max(2, Math.min(6, count)) }),

  updateGameOptions: (updates) => set(state => ({
    gameOptions: { ...state.gameOptions, ...updates },
  })),

  startGame: (controller) => set({
    gameActive: true,
    gameController: controller,
    currentView: 'game',
    won: false,
    winResult: null,
    firstWin: false,
    isNewBest: false,
    guessHistory: [],
    stats: { actors: 0, titles: 0, hintsUsed: 0, won: false, bestPath: null, elapsed: 0 },
    winPanelState: 'closed',
    loading: false,
  }),

  endGame: () => set({
    gameActive: false,
    gameController: null,
  }),

  updateStats: (stats) => set({ stats }),

  addGuess: (guess) => set(state => ({
    guessHistory: [...state.guessHistory, guess],
  })),

  setWin: (result, firstWin, isNewBest) => set({
    won: true,
    winResult: result,
    firstWin,
    isNewBest,
    winPanelState: firstWin ? 'peeked' : 'peeked',
  }),

  setWinPanelState: (state) => set({ winPanelState: state }),

  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),

  addToast: (message, type = 'info') => {
    const id = Date.now() + Math.random();
    set(state => ({
      toasts: [...state.toasts, { id, message, type }],
    }));
    setTimeout(() => {
      set(state => ({
        toasts: state.toasts.filter(t => t.id !== id),
      }));
    }, 3000);
  },

  clearToasts: () => set({ toasts: [] }),

  // Reset everything
  reset: () => set({
    targets: [],
    gameActive: false,
    gameController: null,
    stats: { actors: 0, titles: 0, hintsUsed: 0, won: false, bestPath: null, elapsed: 0 },
    guessHistory: [],
    won: false,
    winResult: null,
    firstWin: false,
    isNewBest: false,
    winPanelState: 'closed',
    loading: false,
    error: null,
    toasts: [],
  }),
}));
